import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import DirectionsCarRounded from "@material-ui/icons/DirectionsCarRounded";
import WarningRounded from "@material-ui/icons/WarningRounded";
import ArrowForward from "@material-ui/icons/ArrowForward";
import Close from "@material-ui/icons/Close";
import TSDialogDatetime from "components/Forms/TSDialogDatetime";
import moment from "moment";
import axios from "utils/httpClient";
import TSSelect2 from "components/Forms/TSSelect2";
import { assignmentPeriod, assignmentResourceOptions, assertAssignmentResources } from "./assignmentUtils";
import useAssignmentAvailability from "./useAssignmentAvailability";

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 16, margin: 16, width: "calc(100% - 32px)" },
  title: { padding: "20px 24px", display: "flex", alignItems: "center", gap: 14 },
  icon: { display: "flex", padding: 12, borderRadius: 14, color: "#4338ca", background: "#eef2ff" },
  titleText: { flex: 1, minWidth: 0 },
  content: { background: "#f8fafc", padding: "20px 24px", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" },
  notice: { display: "flex", alignItems: "flex-start", gap: 12, padding: 16, borderRadius: 12, marginBottom: 20 },
  roundTripNotice: { background: "#fff7ed", border: "1px solid #fed7aa", color: "#7c2d12" },
  singleNotice: { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" },
  summary: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 },
  count: { background: "#e0e7ff", color: "#3730a3", fontWeight: 700 },
  list: { listStyle: "none", margin: "0 0 20px", padding: 0 },
  request: { display: "flex", gap: 12, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, marginBottom: 12 },
  number: { flexShrink: 0, width: 28, height: 28, borderRadius: "50%", background: "#eef2ff", color: "#4338ca", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 },
  requestBody: { flex: 1, minWidth: 0 },
  route: { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 24px minmax(0, 1fr)", gap: 12, alignItems: "center", overflowWrap: "anywhere" },
  tags: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 },
  times: { marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "#f8fafc" },
  form: { border: "1px solid #e2e8f0", borderRadius: 12, padding: 16, margin: "0 0 12px", background: "#fff", minWidth: 0 },
  timeField: { marginTop: 26 },
  error: { padding: 12, borderRadius: 8, background: "#fef2f2", color: "#b91c1c", marginBottom: 12 },
  loading: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "40px 0" },
  actions: { padding: "16px 24px", gap: 8, [theme.breakpoints.down("xs")]: { flexWrap: "wrap", "& > button": { flexGrow: 1 } } },
  actionNote: { flex: 1, color: "#92400e", [theme.breakpoints.down("xs")]: { flexBasis: "100%" } },
  save: { background: "#4338ca", color: "#fff", boxShadow: "none", padding: "10px 18px", borderRadius: 8, "&:hover": { background: "#3730a3", boxShadow: "none" } }
}));
const formatDate = (value, pattern = "DD/MM/BBBB HH:mm") => value && moment(value).isValid() ? moment(value).format(pattern) : "-";

export default function VehicleAssignmentTurnEditor({ turnId, goBackKey, onClose, onSaved }) {
  const classes = useStyles();
  const theme = useTheme();
  const paperRef = useRef(null);
  const contentRef = useRef(null);
  const [menuPortalTarget, setMenuPortalTarget] = useState(null);
  const attachPaper = useCallback(node => {
    paperRef.current = node;
    // Escape the paper's overflow while keeping menus inside the dialog's focus trap.
    if (node) setMenuPortalTarget(node.parentElement);
  }, []);
  const closeMenuOnScroll = useCallback(event => event.target === paperRef.current || event.target === contentRef.current, []);
  const dialogSelectProps = {
    menuPortalTarget, menuPosition: "fixed", menuPlacement: "auto", menuShouldScrollIntoView: false,
    closeMenuOnScroll,
    styles: { menuPortal: base => ({ ...base, zIndex: theme.zIndex.modal + 1 }) }
  };
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const roundTrip = details?.turnKey === 1;
  const directionLabel = (details?.turnKey === 3 || (!details && goBackKey === 2)) ? "ขากลับ" : "ขาไป";
  const saveLabel = roundTrip ? "บันทึกทั้งขาไปและขากลับ" : "บันทึกการแก้ไขเที่ยว";
  const goPassengers = details?.bookings?.filter(b => b.outbound).reduce((sum, b) => sum + (Number(b.totalPassenger) || 0), 0) || 0;
  const backPassengers = details?.bookings?.filter(b => b.return).reduce((sum, b) => sum + (Number(b.totalPassenger) || 0), 0) || 0;
  const requiredCapacity = details?.turnKey === 1 ? Math.max(goPassengers, backPassengers)
    : details?.turnKey === 2 ? goPassengers : details?.turnKey === 3 ? backPassengers : 0;
  const availability = useAssignmentAvailability(form, turnId);
  const options = assignmentResourceOptions(availability.data, form, [], -1, requiredCapacity);
  const driverValue = options.drivers.find(option => option.value.toLowerCase() === form.driverUserName?.toLowerCase())?.value || form.driverUserName;
  useEffect(() => {
    let current = true;
    setLoading(true); setDetails(null); setError("");
    axios.get(`/api/v1/vehiclebooking/assignments/turns/${turnId}`, { params: { goBackKey } }).then(response => {
      if (!current) return;
      if (response.data.result !== 200) throw new Error(response.data.msg);
      const detail = response.data.data;
      setDetails(detail);
      setForm({ startDatetime: moment(detail.startDatetime).format("YYYY-MM-DD"), startTime: detail.startTime,
        endDatetime: moment(detail.endDatetime).format("YYYY-MM-DD"), endTime: detail.endTime,
        vehicleKey: detail.vehicleKey, driverUserName: detail.driverUserName || "", version: detail.version, goBackKey });
      setLoading(false);
    }).catch(err => { if (current) { setError(err.response?.data?.msg || "ไม่สามารถโหลดข้อมูลเที่ยวรถได้"); setLoading(false); } });
    return () => { current = false; };
  }, [turnId, goBackKey, reload]);
  const change = (field, value) => { setForm(previous => ({ ...previous, [field]: value })); setError(""); };
  const close = () => { if (!savingRef.current) onClose(); };
  const save = async () => {
    if (savingRef.current) return;
    try {
      if (!assignmentPeriod(form)) throw new Error("กรุณาระบุช่วงเวลาให้ถูกต้อง");
      assertAssignmentResources(form, options, availability.ready, requiredCapacity);
    } catch (err) { setError(err.message); return; }
    savingRef.current = true; setSaving(true); setError("");
    try {
      const response = await axios.put(`/api/v1/vehiclebooking/assignments/turns/${turnId}`, form);
      if (response.data.result !== 200) throw new Error(response.data.msg);
      setSaving(false);
      onSaved(response.data.msg || "แก้ไขเที่ยวรถเรียบร้อยแล้ว");
    } catch (err) {
      setSaving(false);
      setError(err.response?.data?.msg || err.message || "ไม่สามารถแก้ไขเที่ยวรถได้");
      if (err.response?.status === 409) availability.refresh();
    } finally { savingRef.current = false; }
  };
  return (
    <Dialog open fullWidth maxWidth="md" PaperProps={{ ref: attachPaper, className: classes.paper }} onClose={close}
      aria-labelledby="edit-assignment-turn-title" aria-describedby="edit-assignment-turn-scope">
      <DialogTitle disableTypography className={classes.title}>
        <div className={classes.icon}><DirectionsCarRounded /></div>
        <div className={classes.titleText}>
          <Typography variant="h6" id="edit-assignment-turn-title" style={{ fontWeight: 700 }}>{!details ? "แก้ไขเที่ยวรถ" : roundTrip ? "แก้ไขเที่ยวรถไป–กลับ" : `แก้ไขเที่ยว${directionLabel}`}</Typography>
          <Typography variant="body2" color="textSecondary">ปรับวันเวลา รถ และผู้ขับสำหรับคำขอที่จัดร่วมเที่ยว</Typography>
        </div>
        <IconButton aria-label="ปิดหน้าต่างแก้ไขเที่ยวรถ" disabled={saving} onClick={close}><Close /></IconButton>
      </DialogTitle>
      <DialogContent ref={contentRef} className={classes.content}>
        {error && <div role="alert" className={classes.error}>{error}</div>}
        {loading ? <div className={classes.loading}><CircularProgress size={24} aria-label="กำลังโหลดเที่ยวรถ" /><Typography>กำลังโหลดรายละเอียดเที่ยวรถ...</Typography></div> : details && <>
          <div id="edit-assignment-turn-scope" role="note" className={`${classes.notice} ${roundTrip ? classes.roundTripNotice : classes.singleNotice}`}>
            {roundTrip && <WarningRounded style={{ flexShrink: 0 }} />}
            <div>
              <Typography style={{ fontWeight: 700, marginBottom: 4 }}>{roundTrip ? "เที่ยวรถนี้เป็นเที่ยวไปกลับ" : `รอบที่แก้ไข: ${directionLabel}`}</Typography>
              <Typography variant="body2">{roundTrip
                ? "หากแก้ไขรถ ผู้ขับ หรือวันเวลา จะมีผลกับทั้ง 2 เที่ยว (ขาไปและขากลับ) พร้อมกัน รวมถึงทุกคำขอที่จัดร่วมเที่ยวนี้"
                : `บันทึกเฉพาะ${directionLabel}ของคำขอที่อยู่ในรอบนี้`}</Typography>
            </div>
          </div>
          <div className={classes.summary}><Typography style={{ fontWeight: 700 }}>คำขอที่ได้รับผลจากการแก้ไข</Typography>
            <Chip size="small" className={classes.count} label={`${details.bookings.length} คำขอ`} />
            <Chip size="small" label={roundTrip ? "ไป–กลับ" : directionLabel} />
          </div>
          <ol className={classes.list} aria-label="คำขอที่อยู่ในเที่ยวรถนี้">
            {details.bookings.map((booking, index) => {
              const oneWay = Number(booking.isOneWay) === 1;
              return <li key={booking.vbid} className={classes.request}>
                <span className={classes.number}>{index + 1}</span>
                <div className={classes.requestBody}>
                  <div className={classes.route}>
                    <div><Typography variant="caption" color="textSecondary">ต้นทาง</Typography><Typography style={{ fontWeight: 700 }}>{booking.startPlace || "-"}</Typography></div>
                    <ArrowForward fontSize="small" style={{ color: "#94a3b8" }} />
                    <div><Typography variant="caption" color="textSecondary">ปลายทาง</Typography><Typography style={{ fontWeight: 700 }}>{booking.endPlace || "-"}</Typography></div>
                  </div>
                  {booking.vbObjective && <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>{booking.vbObjective}</Typography>}
                  <div className={classes.tags}>
                    {booking.outbound && <Chip size="small" label="ขาไป" />}
                    {booking.return && !oneWay && <Chip size="small" label="ขากลับ" />}
                    {oneWay && <Chip size="small" label="เที่ยวเดียว" />}
                  </div>
                  {(booking.startDatetime || booking.endDatetime) && <div className={classes.times}>
                    <Typography variant="caption" color="textSecondary">วันเวลาที่ขอใช้</Typography>
                    {booking.outbound && <Typography variant="body2">ขาไป: {formatDate(booking.startDatetime)}</Typography>}
                    {booking.return && !oneWay && <Typography variant="body2">ขากลับ: {formatDate(booking.endDatetime)}</Typography>}
                  </div>}
                </div>
              </li>;
            })}
          </ol>
          <fieldset disabled={saving} className={classes.form} style={{ pointerEvents: saving ? "none" : "auto" }}>
            <Typography style={{ fontWeight: 700, marginBottom: 16 }}>วันเวลาและรถที่จัดไว้</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>{roundTrip ? "วันที่ขาไป" : "วันที่เริ่มต้นเที่ยว"}</Typography><TSDialogDatetime portalTarget={menuPortalTarget} disabled={saving} value={moment(form.startDatetime)} dateFormat="DD/MM/BBBB" timeFormat={false}
                inputProps={{ "aria-label": "วันที่เริ่มต้นเที่ยว", readOnly: true }} onChange={value => { if (moment.isMoment(value)) change("startDatetime", value.format("YYYY-MM-DD")); }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth variant="outlined" size="small" className={classes.timeField} type="time" label={roundTrip ? "เวลาออกรถขาไป" : "เวลาเริ่มต้นเที่ยว"} value={form.startTime || ""} InputLabelProps={{ shrink: true }} onChange={event => change("startTime", event.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>{roundTrip ? "วันที่ขากลับ" : "วันที่สิ้นสุดเที่ยว"}</Typography><TSDialogDatetime portalTarget={menuPortalTarget} disabled={saving} value={moment(form.endDatetime)} dateFormat="DD/MM/BBBB" timeFormat={false}
                inputProps={{ "aria-label": "วันที่สิ้นสุดเที่ยว", readOnly: true }} onChange={value => { if (moment.isMoment(value)) change("endDatetime", value.format("YYYY-MM-DD")); }} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth variant="outlined" size="small" className={classes.timeField} type="time" label={roundTrip ? "เวลารับขากลับ" : "เวลาสิ้นสุดเที่ยว"} value={form.endTime || ""} InputLabelProps={{ shrink: true }} onChange={event => change("endTime", event.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TSSelect2 {...dialogSelectProps} label="รถ" options={options.vehicles} value={form.vehicleKey} isDisabled={saving || !availability.ready} onChange={option => change("vehicleKey", option?.value || null)} /></Grid>
              <Grid item xs={12} sm={6}><TSSelect2 {...dialogSelectProps} label="ผู้ขับรถ" options={options.drivers} value={driverValue} isDisabled={saving || !availability.ready} onChange={option => change("driverUserName", option?.value || "")} /></Grid>
            </Grid>
          </fieldset>
          {!availability.rangeValid && <Typography color="error">กรุณาระบุช่วงเวลาให้ถูกต้องก่อนเลือกรถและผู้ขับ</Typography>}
          {availability.loading && <Typography>กำลังตรวจสอบรถและผู้ขับ...</Typography>}
          {availability.error && <Typography role="alert" color="error">{availability.error}</Typography>}
          {availability.ready && !options.drivers.length && <Typography color="error">ยังไม่มีคนขับที่พร้อมใช้งาน <a href="/vehicle/drivers">จัดการคนขับรถ</a></Typography>}
          {availability.error && <Button onClick={availability.refresh} disabled={saving}>ตรวจสอบอีกครั้ง</Button>}
        </>}
      </DialogContent>
      <DialogActions className={classes.actions}>
        {roundTrip && <Typography variant="caption" className={classes.actionNote}>บันทึกการเปลี่ยนแปลงทั้ง 2 เที่ยวพร้อมกัน</Typography>}
        {error && <Button disabled={saving} onClick={() => setReload(value => value + 1)}>โหลดข้อมูลเที่ยวใหม่</Button>}
        <Button disabled={saving} onClick={close}>ปิด</Button>
        <Button variant="contained" className={classes.save} disabled={loading || saving || !details || !availability.ready} onClick={save}>{saving ? "กำลังบันทึก..." : saveLabel}</Button>
      </DialogActions>
    </Dialog>
  );
}

VehicleAssignmentTurnEditor.propTypes = {
  turnId: PropTypes.string.isRequired,
  goBackKey: PropTypes.oneOf([1, 2]).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};
